import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useUser } from "contexts/UserContext";
import { resolvePortalPath, stripPortalBasePath } from "utils/portalPath";
import { MenuOrderSettingsPanel, routePathToMenuId, useMobileNavigationOrder, getRouteLabel, menuIdToRoutePath } from "./MenuOrderSettings";

const getActiveRoute = (currentPath, routePath) => {
  if (routePath === "lead-management") {
    return currentPath.startsWith("lead-management");
  }
  if (routePath === "client-management") {
    return currentPath.startsWith("client-management");
  }
  if (routePath === "careof-management") {
    return currentPath.startsWith("careof-management");
  }
  if (routePath === "staff-management") {
    return currentPath.startsWith("staff-management");
  }
  if (routePath === "folders/leads") {
    return /^folders\/leads(?:\/|$)/.test(currentPath);
  }
  if (routePath === "folders/clients") {
    return /^folders\/clients(?:\/|$)/.test(currentPath);
  }
  return currentPath === routePath || currentPath.startsWith(`${routePath}/`);
};


const MobileBottomNav = () => {
  const location = useLocation();
  const { user } = useUser();
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const { visibleRoutes, mobileOrderIds, setMobileOrderIds, saving, saveMobileOrder } = useMobileNavigationOrder();

  const orderedRoutes = useMemo(() => {
    const routeByPath = new Map(visibleRoutes.map((route) => [route.path, route]));

    if (Array.isArray(mobileOrderIds) && mobileOrderIds.length > 0) {
      const ordered = [];
      mobileOrderIds.forEach((menuId) => {
        const path = menuIdToRoutePath[menuId];
        const route = routeByPath.get(path);
        if (route) ordered.push(route);
      });
      visibleRoutes.forEach((route) => {
        if (!ordered.some((item) => item.path === route.path)) ordered.push(route);
      });
      return ordered;
    }

    return visibleRoutes;
  }, [mobileOrderIds, visibleRoutes]);

  const navigate = useNavigate();
  const currentPath = stripPortalBasePath(location.pathname).replace(/^\/+|\/+$/g, "");

  const labelMap = {
    dashboard: "Dashboard",
    calendar: "Calendar",
    "careof-management": "CareOf",
    "lead-management": "Leads",
    "client-management": "Clients",
    "staff-management": "Staff",
    "folders/leads": "Lead Folders",
    "folders/clients": "Client Folders",
  };

  const getRouteLabel = (route) => {
    return labelMap[route.path] || route.name;
  };

  if (orderedRoutes.length === 0) return null;

  return (
    <div className="md:hidden">
      <div className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-200 bg-white/95 px-2 py-2 backdrop-blur-xl dark:border-white/10 dark:bg-navy-900/95">
        <div className="mb-1 flex items-center justify-between px-2 text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-300">
          <div />
          <button
            type="button"
            onClick={() => setIsSettingsOpen((v) => !v)}
            className="rounded-full border border-gray-200 bg-white px-3 py-1 text-[11px] text-brand-500 transition hover:border-brand-300 hover:bg-brand-50 dark:border-white/10 dark:bg-navy-800 dark:text-brand-300 dark:hover:bg-navy-700"
          >
            Settings
          </button>
        </div>

        {isSettingsOpen && (
          <MenuOrderSettingsPanel
            mobileOrderIds={mobileOrderIds}
            visibleRoutes={visibleRoutes}
            onOrderChange={setMobileOrderIds}
            onSave={(ids) => {
              saveMobileOrder(ids);
              setIsSettingsOpen(false);
            }}
            onClose={() => setIsSettingsOpen(false)}
            saving={saving}
          />
        )}

        <div className="flex items-center justify-start gap-2 overflow-x-auto px-1 no-scrollbar pb-1">
          {orderedRoutes.map((route) => {
            const isActive = getActiveRoute(currentPath, route.path);
            return (
              <Link
                key={route.path}
                to={resolvePortalPath(route.path, user?.role)}
                className={`flex-none flex min-w-max flex-col items-center justify-center gap-1 rounded-3xl px-3 py-2 text-center transition ${
                  isActive
                    ? "bg-brand-500 text-white shadow-brand/20"
                    : "text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-navy-800"
                }`}
                aria-current={isActive ? "page" : undefined}
              >
                <span className="flex h-8 w-8 items-center justify-center rounded-2xl transition duration-200">
                  {route.icon}
                </span>
                <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.02em]">
                  {getRouteLabel(route)}
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default MobileBottomNav;
